export type developmentGoals = {
	label: string;
	inputFieldName: string;
	fileUploaderName: string;
}[];

export interface CommonPropertiesOfProjectProposal {
	// basics
	id?: number;
	title: string;
	summary: string;
	removalReduction: string;
	primarySectoralScope: string;
	secondarySectoralScope: string;
	location: string;
	collaborators: string;
	startDate: string;
	endDate: string;
	// methodology
	methodologyId: number;
	// safeguards
	developmentGoals: developmentGoals;
	environmental_risk: string;
	environmental_risk_file: string;
	environmental_benefits: string;
	environmental_benefits_file: string;
	biodiversity_benifits: string;
	biodiversity_benifits_file: string;
	biodiversity_threads: string;
	biodiversity_threads_file: string;
	pollution_preventaion: string;
	pollution_preventaion_file: string;
	social_risk: string;
	social_risk_file: string;
	social_benefit: string;
	social_benefit_file: string;
	community_benefits: string;
	community_benefits_file: string;
	jobs: string;
	jobs_file: string;
	labour_rights: string;
	labour_rights_file: string;
	fair_treatment: string;
	fair_treatment_file: string;
	child_labour: string;
	child_labour_file: string;
	contract_workers: string;
	contract_workers_file: string;
	indigenous_local_knowledge: string;
	indigenous_local_knowledge_file: string;
	community: string;
	community_file: string;
	gender_equity: string;
	gender_equity_file: string;
	lands_terrories: string;
	lands_terrories_file: string;
	cultural_heritage: string;
	cultural_heritage_file: string;
	public_health: string;
	public_health_file: string;
	NO_POVERTY: string;
	NO_POVERTY_FILE: string;
	ZERO_HUNGER: string;
	ZERO_HUNGER_FILE: string;
	GOOD_HEALTH_AND_WELL_BEING: string;
	GOOD_HEALTH_AND_WELL_BEING_FILE: string;
	QUALITY_EDUCATION: string;
	QUALITY_EDUCATION_FILE: string;
	GENDER_EQUALITY: string;
	GENDER_EQUALITY_FILE: string;
	CLEAN_WATER_AND_SANITATION: string;
	CLEAN_WATER_AND_SANITATION_FILE: string;
	AFFORDABLE_AND_CLEAN_ENERGY: string;
	AFFORDABLE_AND_CLEAN_ENERGY_FILE: string;
	DECENT_WORK_AND_ECONOMIC_GROWTH: string;
	DECENT_WORK_AND_ECONOMIC_GROWTH_FILE: string;
	INDUSTRY_INNOVATION_AND_INFRASTRUCTURE: string;
	INDUSTRY_INNOVATION_AND_INFRASTRUCTURE_FILE: string;
	REDUCED_INEQUALITIES: string;
	REDUCED_INEQUALITIES_FILE: string;
	RESPONSIBLE_CONSUMPTION_AND_PRODUCTION: string;
	RESPONSIBLE_CONSUMPTION_AND_PRODUCTION_FILE: string;
	CLIMATE_ACTION: string;
	CLIMATE_ACTION_FILE: string;
	LIFE_BELOW_WATER: string;
	LIFE_BELOW_WATER_FILE: string;
	LIFE_ON_LAND: string;
	LIFE_ON_LAND_FILE: string;
	PEACE_JUSTICE_AND_STRONG_INSTITUTIONS: string;
	PEACE_JUSTICE_AND_STRONG_INSTITUTIONS_FILE: string;
	PARTNERSHIPS_FOR_THE_GOALS: string;
	PARTNERSHIPS_FOR_THE_GOALS_FILE: string;
	// validity
	projectValidityOwnershipFile: string;
	eligibility: string;
	eligibilityFile: string;
	baselineScenario: string;
	baselineScenarioFile: string;
	permanence: string;
	permanenceFile: string;
	additionality: string;
	additionalityFile: string;
	leakage: string;
	leakageFile: string;
	consultations: string;
	publicEngagementFile: string;
	other: string;
	otherFile: string;
	// monitoring
	monitoringEvents: string;
	estimatedEmissionReduction: string;
	bufferPool: number;
	ctd: CtdFieldRow[];
	alres: AlresFieldRow[];
}

export interface CtdFieldRow {
	ctdUnit: string;
	ctdValue: number;
	ctdSource: string;
}

export interface AlresFieldRow {
	alresUnit: string;
	alresMeasuringPlan: string;
}

type InReviewProjectProposal = CommonPropertiesOfProjectProposal & {
	status: "IN_REVIEW";
};

type ApprovedProjectProposal = CommonPropertiesOfProjectProposal & {
	status: "APPROVED";
};

type SubmittedOrDraftProjectProposal = CommonPropertiesOfProjectProposal & {
	status: "SUBMITTED" | "DRAFT";
};

export type ProjectProposal = InReviewProjectProposal | ApprovedProjectProposal | SubmittedOrDraftProjectProposal;

export type ValidationFileType = {
	description: string;
	entryId: number;
	fileId: number;
	fileName: string;
	fileSize: string;
	fileType: string;
	fileUrl: string;
	title: string;
	name: string;
	uploaderUserId: number;
};

export interface ProjectDetails {
	id: number;
	status: string;
	submittedAt: string | null;
	isRegisteringOnlyInOcp: boolean | null;
	title: string;
	summary: string;
	type: string;
	primaryLocation?: string;
	locations: ProjectProposalLocation[];
	collaborators: ProjectProposalCollaborator[];
	startAt: string;
	endAt: string;
	publicCommentaryStartDate?: string;
	publicCommentaryEndDate?: string;
	validationStartedAt?: string | null;
	publicCommentaryEndsAt?: string | null;
	inPublicCommentary?: boolean;
	environmentalImpact: any;
	socialImpact: any;
	otherImpact: any;
	ownershipCompliance: any;
	eligibility: any;
	projectBoundary: any;
	methodologyDeviations: any;
	baselineScenario: any;
	permanence: any;
	additionality: any;
	leakage: any;
	consultations: any;
	otherValidity: any;
	emissionReduction: any;
	qualitativeMonitoringPlan: any;
	createdAt: string;
	updatedAt: string;
	sectoralScopeId: number;
	originatorId: number;
	methodologyId: number | null;
	methodologyVersionName?: string;
	methodology?: {
		id: number;
		title: string;
		primarySectoralScope: string;
		secondarySectoralScope: string;
		originator: string;
	};
	monitoringEvents: { id?: number; eventDate: string }[] | null;
	bufferPool: number | null;
	originatorEmail: string;
	originatorName: string;
	sectoralScope: {
		primary: string;
		secondary: string;
	};
	validatorList: any[];
	validateDecisionFileList?: ValidationDecisionFile[];
	fileList: ProjectDetailsFile[];
	publicCommentaryFileList: ProjectDetailsFile[];
	sdgList: any[];
	variableList: ProjectDetailsVariableObject[];
	validationFileList: ValidationFileType[];
	postValidationFileList: ValidationFileType[];
	projectImageFileList: {
		fileId: number;
		fileUrl: string;
		fileName: string;
		fileSize: string;
		fileType: string;
		sequence: number;
	}[];
	//safeguards descriptions
	environmentalRisks: string;
	environmentalBenefits: string;
	bioDiversityThreats: string;
	bioDiversityBenefits: string;
	pollutionPrevention: string;
	socialRisks: string;
	socialBenefits: string;
	communityBenefits: string;
	jobs: string;
	laborRights: string;
	fairTreatment: string;
	noChildLabor: string;
	contractWorkersProtection: string;
	indigenousLocalKnowledgeUtilization: string;
	communityConsideration: string;
	genderEquity: string;
	noNegativeImpactInLandAndTerritories: string;
	culturalHeritage: string;
	publicHealth: string;
	reddPlusCancun: string;
	waterUseAndEfficiency: string;
	soilAndLandProtection: string;
	habitatAndEcosystemProtection: string;
	issuedCredits: {
		issuedCredits: number;
		retiredCredits: number;
		availableCredits: number;
		bufferCredits: number;
	};
}

export interface ProjectDetailsVariableObject {
	variableId: number;
	variableName: string;
	variableType: string;
	variableAbbreviation: string;
	unit: string;
	source?: string;
	value?: string;
	measuringPlan?: string;
	justification?: string;
	isOptional?: boolean;
}

export type ProjectDetailsFile = {
	fileId: number;
	fileUrl: string;
	fileName: string;
	fileSize: string;
	fileType: ProjectFileType;
};

export type ValidationDecisionFile = {
	fileId: number;
	fileUrl: string;
	fileName: string;
	fileSize: string;
	fileType: "PROJECT_VALIDATE_DECISION_SITE_VISIT" | "PROJECT_VALIDATE_DECISION_NOTES";
};

export enum ValidationDecisionFileType {
	PROJECT_VALIDATE_DECISION_SITE_VISIT = "PROJECT_VALIDATE_DECISION_SITE_VISIT",
	PROJECT_VALIDATE_DECISION_NOTES = "PROJECT_VALIDATE_DECISION_NOTES",
}

export interface projectFilesMappedType {
	[key: string]: ProjectDetailsFile;
}

export enum ProjectFileType {
	PROJECT_VALIDITY_OWNERSHIP = "PROJECT_VALIDITY_OWNERSHIP",
	PROJECT_VALIDITY_ELIGIBILITY = "PROJECT_VALIDITY_ELIGIBILITY",
	PROJECT_BASELINE_SCENARIO = "PROJECT_BASELINE_SCENARIO",
	PROJECT_VALIDITY_PERMANENCE = "PROJECT_VALIDITY_PERMANENCE",
	PROJECT_VALIDITY_ADDITIONALITY = "PROJECT_VALIDITY_ADDITIONALITY",
	PROJECT_VALIDITY_LEAKAGE = "PROJECT_VALIDITY_LEAKAGE",
	PROJECT_VALIDITY_CONSULTATION = "PROJECT_VALIDITY_CONSULTATION",
	PROJECT_VALIDITY_OTHER = "PROJECT_VALIDITY_OTHER",
	PROJECT_MONITORING_QUALITATIVE = "PROJECT_MONITORING_QUALITATIVE",
	PROJECT_MONITORING_MEASURING_PLAN = "PROJECT_MONITORING_MEASURING_PLAN",
	PROJECT_VALIDITY_METHODOLOGY_DEVIATIONS = "PROJECT_VALIDITY_METHODOLOGY_DEVIATIONS",
	PROJECT_VALIDITY_PROJECT_BOUNDARY = "PROJECT_VALIDITY_PROJECT_BOUNDARY",
	PROJECT_SAFEGUARDS_ENVIRONMENTAL_RISKS = "PROJECT_SAFEGUARDS_ENVIRONMENTAL_RISKS",
	PROJECT_SAFEGUARDS_ENVIRONMENTAL_BENEFITS = "PROJECT_SAFEGUARDS_ENVIRONMENTAL_BENEFITS",
	PROJECT_SAFEGUARDS_BIO_DIVERSITY_THREATS = "PROJECT_SAFEGUARDS_BIO_DIVERSITY_THREATS",
	PROJECT_SAFEGUARDS_BIO_DIVERSITY_BENEFITS = "PROJECT_SAFEGUARDS_BIO_DIVERSITY_BENEFITS",
	PROJECT_SAFEGUARDS_POLLUTION_PREVENTION = "PROJECT_SAFEGUARDS_POLLUTION_PREVENTION",
	PROJECT_SAFEGUARDS_SOCIAL_RISKS = "PROJECT_SAFEGUARDS_SOCIAL_RISKS",
	PROJECT_SAFEGUARDS_SOCIAL_BENEFITS = "PROJECT_SAFEGUARDS_SOCIAL_BENEFITS",
	PROJECT_SAFEGUARDS_COMMUNITY_BENEFITS = "PROJECT_SAFEGUARDS_COMMUNITY_BENEFITS",
	PROJECT_SAFEGUARDS_JOBS = "PROJECT_SAFEGUARDS_JOBS",
	PROJECT_SAFEGUARDS_LABOR_RIGHTS = "PROJECT_SAFEGUARDS_LABOR_RIGHTS",
	PROJECT_SAFEGUARDS_FAIR_TREATMENT = "PROJECT_SAFEGUARDS_FAIR_TREATMENT",
	PROJECT_SAFEGUARDS_NO_CHILD_LABOR = "PROJECT_SAFEGUARDS_NO_CHILD_LABOR",
	PROJECT_SAFEGUARDS_CONTRACT_WORKERS_PROTECTION = "PROJECT_SAFEGUARDS_CONTRACT_WORKERS_PROTECTION",
	PROJECT_SAFEGUARDS_INDIGENOUS_LOCAL_KNOWLEDGE_UTILIZATION = "PROJECT_SAFEGUARDS_INDIGENOUS_LOCAL_KNOWLEDGE_UTILIZATION",
	PROJECT_SAFEGUARDS_COMMUNITY_CONSIDERATION = "PROJECT_SAFEGUARDS_COMMUNITY_CONSIDERATION",
	PROJECT_SAFEGUARDS_GENDER_EQUITY = "PROJECT_SAFEGUARDS_GENDER_EQUITY",
	PROJECT_SAFEGUARDS_NO_NEGATIVE_IMPACT_IN_LAND_AND_TERRITORIES = "PROJECT_SAFEGUARDS_NO_NEGATIVE_IMPACT_IN_LAND_AND_TERRITORIES",
	PROJECT_SAFEGUARDS_CULTURAL_HERITAGE = "PROJECT_SAFEGUARDS_CULTURAL_HERITAGE",
	PROJECT_SAFEGUARDS_PUBLIC_HEALTH = "PROJECT_SAFEGUARDS_PUBLIC_HEALTH",
	PROJECT_SAFEGUARDS_REDD_PLUS_CANCUN = "PROJECT_SAFEGUARDS_REDD_PLUS_CANCUN",
	PROJECT_SAFEGUARDS_WATER_USE_AND_EFFICIENCY = "PROJECT_SAFEGUARDS_WATER_USE_AND_EFFICIENCY",
	PROJECT_SAFEGUARDS_SOIL_AND_LAND_PROTECTION = "PROJECT_SAFEGUARDS_SOIL_AND_LAND_PROTECTION",
	PROJECT_SAFEGUARDS_HABITAT_AND_ECOSYSTEM_PROTECTION = "PROJECT_SAFEGUARDS_HABITAT_AND_ECOSYSTEM_PROTECTION",
}

export enum SdgFileType {
	SDG_NO_POVERTY = "SDG_NO_POVERTY",
	SDG_ZERO_HUNGER = "SDG_ZERO_HUNGER",
	SDG_GOOD_HEALTH_AND_WELL_BEING = "SDG_GOOD_HEALTH_AND_WELL_BEING",
	SDG_QUALITY_EDUCATION = "SDG_QUALITY_EDUCATION",
	SDG_GENDER_EQUALITY = "SDG_GENDER_EQUALITY",
	SDG_CLEAN_WATER_AND_SANITATION = "SDG_CLEAN_WATER_AND_SANITATION",
	SDG_AFFORDABLE_AND_CLEAN_ENERGY = "SDG_AFFORDABLE_AND_CLEAN_ENERGY",
	SDG_DECENT_WORK_AND_ECONOMIC_GROWTH = "SDG_DECENT_WORK_AND_ECONOMIC_GROWTH",
	SDG_INDUSTRY_INNOVATION_AND_INFRASTRUCTURE = "SDG_INDUSTRY_INNOVATION_AND_INFRASTRUCTURE",
	SDG_REDUCED_INEQUALITIES = "SDG_REDUCED_INEQUALITIES",
	SDG_SUSTAINABLE_CITIES_AND_COMMUNITIES = "SDG_SUSTAINABLE_CITIES_AND_COMMUNITIES",
	SDG_RESPONSIBLE_CONSUMPTION_AND_PRODUCTION = "SDG_RESPONSIBLE_CONSUMPTION_AND_PRODUCTION",
	SDG_CLIMATE_ACTION = "SDG_CLIMATE_ACTION",
	SDG_LIFE_BELOW_WATER = "SDG_LIFE_BELOW_WATER",
	SDG_LIFE_ON_LAND = "SDG_LIFE_ON_LAND",
	SDG_PEACE_JUSTICE_AND_STRONG_INSTITUTIONS = "SDG_PEACE_JUSTICE_AND_STRONG_INSTITUTIONS",
	SDG_PARTNERSHIPS_FOR_THE_GOALS = "SDG_PARTNERSHIPS_FOR_THE_GOALS",
}

interface ProjectProposalCollaborator {
	id?: string;
	name: string;
	role: string;
	email: string;
}

interface ProjectProposalLocation {
	id?: string;
	name: string;
	purpose: string;
}

export type ProjectComment = {
	commentId: number;
	comment: string;
	parentCommentId: number | null;
	userId: number;
	name: string;
	date: string;
};
